/**
 * `AmmUserActivity` (amm activity.v0) → graduated-markets-feed row mapper.
 *
 * Subject: `morfi.amm.${mint}.user.*.activity.v0` (proto
 * `morfi.v1.AmmUserActivity`, payload `{ market, outcomeIdx, amountD6,
 * postOutcomePriceD9, postQuoteReservesD6, … }`).
 *
 * The graduated feed is a list with ONE row per graduated market (the
 * winning outcome flattened onto the market row). The codegen dispatcher runs
 * this helper LIST-WIDE (once per cached row) with the mint captured from the
 * subject (`morfi.amm.${mint}.user.*.activity.v0`) passed as `subjectMint`;
 * this helper gates on it, applying only to the row whose `mint` matches. The
 * The committed payload also carries market/outcome identity. The list reducer
 * still keys on the subject mint because the flattened graduated row is
 * outcome-mint keyed and the dispatcher already scopes the event to that mint.
 *
 * Applies: `price = postOutcomePriceD9` (D9), `liquidity =
 * postQuoteReservesD6` (D6), `volumeTotal += amountD6` (D6). Price-derived
 * fields are ALSO recomputed live (no supply / window-reference needed in
 * the event): `marketCap` scales with the price ratio (supply is constant
 * across AMM swaps), and `change15mBps` / `change1hBps` / `change24hBps` are
 * re-anchored off the row's implied window-open price (recoverable from the
 * current `price` + `bps`). The next REST refresh re-anchors them against the
 * server's exact supply / candle aggregates.
 *
 * Immutable: returns a NEW object only when fields change; otherwise the
 * exact `prev` reference (idempotent on JetStream replay, keeps the
 * structural-sharing `row === prev` short-circuit intact). Platform-
 * agnostic — pure data, no web/RN API. Safe in core.
 */

import { applyAmmActivityToOutcome } from './apply-trade-to-outcome.js';

export function applyAmmActivityToGraduatedRow<
  T extends {
    readonly mint?: string;
    readonly pubkey?: string;
    readonly price?: string;
    readonly liquidity?: string;
    readonly volumeTotal?: string;
    readonly marketCap?: string;
    readonly change15mBps?: string | null;
    readonly change1hBps?: string | null;
    readonly change24hBps?: string | null;
    readonly indexedOutcomeMintSupplyD6?: string;
  },
>(prev: T, raw: Record<string, unknown>, _args: Record<string, unknown>, subjectMint?: string): T {
  const canonical = applyAmmActivityToOutcome(
    {
      ...(prev.mint === undefined ? {} : { mint: prev.mint }),
      ...(prev.price === undefined ? {} : { price: prev.price }),
      ...(prev.liquidity === undefined ? {} : { cpQuoteReserveD6: prev.liquidity }),
      ...(prev.volumeTotal === undefined ? {} : { volumeTotal: prev.volumeTotal }),
      ...(prev.marketCap === undefined ? {} : { effectiveMarketCapD6: prev.marketCap }),
      ...(prev.indexedOutcomeMintSupplyD6 === undefined
        ? {}
        : { indexedOutcomeMintSupplyD6: prev.indexedOutcomeMintSupplyD6 }),
      ...(prev.change15mBps === undefined ? {} : { change15mBps: prev.change15mBps }),
      ...(prev.change1hBps === undefined ? {} : { change1hBps: prev.change1hBps }),
      ...(prev.change24hBps === undefined ? {} : { change24hBps: prev.change24hBps }),
    },
    raw,
    _args,
    subjectMint,
  );
  const changed =
    canonical.price !== prev.price ||
    canonical.cpQuoteReserveD6 !== prev.liquidity ||
    canonical.volumeTotal !== prev.volumeTotal ||
    canonical.effectiveMarketCapD6 !== prev.marketCap ||
    canonical.change15mBps !== prev.change15mBps ||
    canonical.change1hBps !== prev.change1hBps ||
    canonical.change24hBps !== prev.change24hBps;
  if (!changed) return prev;
  return {
    ...prev,
    ...(canonical.price === undefined ? {} : { price: canonical.price }),
    ...(canonical.cpQuoteReserveD6 === undefined ? {} : { liquidity: canonical.cpQuoteReserveD6 }),
    ...(canonical.volumeTotal === undefined ? {} : { volumeTotal: canonical.volumeTotal }),
    ...(canonical.effectiveMarketCapD6 === undefined
      ? {}
      : { marketCap: canonical.effectiveMarketCapD6 }),
    ...(canonical.change15mBps === undefined ? {} : { change15mBps: canonical.change15mBps }),
    ...(canonical.change1hBps === undefined ? {} : { change1hBps: canonical.change1hBps }),
    ...(canonical.change24hBps === undefined ? {} : { change24hBps: canonical.change24hBps }),
  };
}
